if (!customElements.get('aplovio-select')) customElements.define('aplovio-select', class extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    const select = this.querySelector('select');
    if (!select || select.multiple || select.size > 1 || select.querySelector('optgroup')) return;
    this.select = select;
    this.abort = new AbortController();
    const options = {signal: this.abort.signal};
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'ap-input ap-select-trigger';
    this.button.id = select.id + '-trigger';
    this.button.setAttribute('role', 'combobox');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', select.id + '-options');
    this.button.innerHTML = '<span></span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
    this.list = document.createElement('div');
    this.list.className = 'ap-select-options';
    this.list.id = select.id + '-options';
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;
    this.label = select.labels?.[0];
    if (this.label) {
      this.labelFor = this.label.htmlFor;
      this.label.htmlFor = this.button.id;
      this.button.setAttribute('aria-label', this.label.textContent.trim());
      this.list.setAttribute('aria-label', this.label.textContent.trim());
    } else this.button.setAttribute('aria-label', select.getAttribute('aria-label') || select.name);
    this.append(this.button, this.list);
    select.hidden = true;
    this.sync();
    select.addEventListener('change', () => this.sync(), options);
    select.addEventListener('invalid', event => {event.preventDefault(); this.button.setAttribute('aria-invalid','true'); this.button.focus();}, options);
    select.form?.addEventListener('reset', () => queueMicrotask(() => {if(this.isConnected){this.close();this.sync();}}), options);
    this.button.addEventListener('click', () => this.list.hidden ? this.open() : this.close(), options);
    this.button.addEventListener('keydown', event => this.key(event), options);
    this.list.addEventListener('pointerdown', event => event.preventDefault(), options);
    this.list.addEventListener('click', event => {
      const item = event.target.closest('[data-option-index]');
      if(item && item.getAttribute('aria-disabled') !== 'true') this.choose(Number(item.dataset.optionIndex));
    }, options);
    document.addEventListener('pointerdown', event => {if(!this.contains(event.target)) this.close();}, options);
    this.addEventListener('focusout', event => {if(!this.contains(event.relatedTarget)) this.close();}, options);
    window.addEventListener('resize', () => this.close(), options);
    this.observer = new MutationObserver(() => {this.close();this.sync();});
    this.observer.observe(select,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','selected','label','value','aria-invalid','aria-describedby','required']});
  }
  sync() {
    this.button.querySelector('span').textContent = this.select.selectedOptions[0]?.label || '';
    this.button.disabled = this.select.disabled;
    for(const name of ['aria-invalid','aria-describedby']) {
      if(this.select.hasAttribute(name)) this.button.setAttribute(name,this.select.getAttribute(name));else this.button.removeAttribute(name);
    }
    this.button.setAttribute('aria-required',String(this.select.required));
    this.list.replaceChildren(...[...this.select.options].map((option,index) => {
      const item = document.createElement('div');
      item.className = 'ap-select-option';item.id = this.select.id + '-option-' + index;
      item.dataset.optionIndex = index;item.setAttribute('role','option');
      item.setAttribute('aria-selected',String(option.selected));item.setAttribute('aria-disabled',String(option.disabled));
      const text = document.createElement('span');text.textContent = option.label;item.append(text);
      item.insertAdjacentHTML('beforeend','<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>');
      return item;
    }));
  }
  open() {
    if(this.select.disabled) return;
    this.sync();
    const bounds = this.button.getBoundingClientRect();
    const container = this.closest('.ap-layer__body')?.getBoundingClientRect();
    const below = Math.min(innerHeight,container?.bottom || innerHeight) - bounds.bottom - 16;
    const above = bounds.top - Math.max(0,container?.top || 0) - 16;
    const flip = below < 220 && above > below;
    this.list.toggleAttribute('data-above',flip);
    this.list.style.setProperty('--ap-select-height',Math.max(80,Math.min(320,flip?above:below))+'px');
    this.list.hidden = false;this.button.setAttribute('aria-expanded','true');
    this.activate(Math.max(0,this.select.selectedIndex));
  }
  close() { if(!this.list) return;this.list.hidden=true;this.button.setAttribute('aria-expanded','false');this.button.removeAttribute('aria-activedescendant'); }
  activate(index) {
    this.active=index;
    [...this.list.children].forEach((item,i) => item.toggleAttribute('data-active',i===index));
    const item=this.list.children[index];
    if(item){this.button.setAttribute('aria-activedescendant',item.id);const top=item.offsetTop,bottom=top+item.offsetHeight;if(top<this.list.scrollTop)this.list.scrollTop=top;else if(bottom>this.list.scrollTop+this.list.clientHeight)this.list.scrollTop=bottom-this.list.clientHeight;}
  }
  choose(index) {
    if(this.select.options[index]?.disabled) return;
    this.select.selectedIndex=index;
    this.select.dispatchEvent(new Event('input',{bubbles:true}));
    this.select.dispatchEvent(new Event('change',{bubbles:true}));
    this.close();this.button.focus({preventScroll:true});
  }
  key(event) {
    const {key}=event;
    if(key==='Tab'){this.close();return;}
    if(key==='Escape' && !this.list.hidden){event.preventDefault();event.stopPropagation();this.close();return;}
    if(['ArrowDown','ArrowUp','Home','End'].includes(key)){
      event.preventDefault();const wasClosed=this.list.hidden;if(wasClosed)this.open();
      const enabled=[...this.select.options].map((option,index)=>option.disabled?-1:index).filter(index=>index>=0);
      const position=enabled.indexOf(this.active);
      const index=key==='Home'?enabled[0]:key==='End'?enabled.at(-1):wasClosed?this.active:enabled[Math.max(0,Math.min(enabled.length-1,position+(key==='ArrowDown'?1:-1)))];
      if(index!==undefined)this.activate(index);return;
    }
    if((key==='Enter'||key===' ')&&!this.list.hidden){event.preventDefault();this.choose(this.active);return;}
    if(key.length===1 && key!==' ' && !event.ctrlKey && !event.metaKey && !event.altKey){
      event.preventDefault();clearTimeout(this.typeTimer);this.typed=(this.typed||'')+key.toLocaleLowerCase();
      this.typeTimer=setTimeout(()=>this.typed='',600);
      const index=[...this.select.options].findIndex(option=>!option.disabled&&option.label.toLocaleLowerCase().startsWith(this.typed));
      if(index>=0){if(this.list.hidden)this.open();this.activate(index);}
    }
  }
  disconnectedCallback() {
    this.abort?.abort();this.abort=null;this.observer?.disconnect();clearTimeout(this.typeTimer);
    this.button?.remove();this.list?.remove();if(this.select)this.select.hidden=false;
    if(this.label)this.label.htmlFor=this.labelFor;
  }
});
